import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import { InputObject } from "../../controls/default";
import { Dayjs } from "dayjs";
import { FormInstance } from "antd";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyDepartments,
  getTenants,
  getRewards,
  getPlatformUsers,
} from "../../repository/data.repository";

export interface LoyaltyHistorySearchForm {
  readonly user: InputObject;
  readonly reward: InputObject;
  readonly company: InputObject;
  readonly department: InputObject;
  readonly tenant: InputObject;
  readonly createdOn: Dayjs[];
}

export const LoyaltyHistoryColumns: DataColumn[] = [
  {
    title: "User Name",
    key: "userUserName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Points",
    key: "pointsChange",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Description",
    key: "description",
    width: 192,
    sort: true,
  },
  {
    title: "Reward",
    key: "rewardName",
    width: 192,
    sort: true,
  },
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
  },
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
  {
    title: "Tenant",
    key: "tenantName",
    width: 192,
    sort: true,
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 128,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
];

const loyaltyHistoryQueryMappings: QueryFieldMapping[] = [
  { formField: "user", queryField: "userUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "reward", queryField: "rewardUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "tenant", queryField: "tenantUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapLoyaltyHistoryQuery = (values: LoyaltyHistorySearchForm): SearchQuery =>
  buildSearchQuery(values, loyaltyHistoryQueryMappings);

// Factory so the Department / User dropdowns can read the sibling Company / Department filter values:
// platform-wide by default, narrowing as a company (then department) is chosen. `dependsOn` clears stale children.
export const loyaltyHistorySearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "department",
          label: "Department",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "user",
          label: "User Name",
          dependsOn: ["company", "department"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
            ["departmentUid", form.getFieldValue("department")?.value],
          ]),
        },
        {
          type: "combobox",
          name: "reward",
          label: "Reward",
          request: createSearchRequest(getRewards, "name"),
        },
        {
          type: "combobox",
          name: "tenant",
          label: "Tenant",
          request: createSearchRequest(getTenants, "name"),
        },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
      ],
    },
  ],
});
